/**
 * estimateMark — the Preisspiegel's "geschätzt" marker (#2777).
 *
 * `price_observation` served two populations under one name: prices a buyer paid,
 * and prices the curated book estimates for a crop with no trade behind it
 * (`provenance.estimate: true`, written by `custom_seed_prices` since
 * #2441/#2695). 2777c split them at the source. This module is what stops the
 * second one arriving on screen wearing the first one's clothes — a curated
 * 38 €/t rendered in the same slot, type and colour as a realised price is the
 * #2695 false-green, and it is worse than hiding the number altogether.
 *
 * ── Why a SECOND estimateMark, next to the Deckungsbeitrag one ────────────────
 *
 * The v31.8 module (`deckungsbeitrag/estimateMark.tsx`) is deliberately not
 * imported here, and its own header says why: its copy is Leistung-grain-specific
 * ("für diesen Schlag ist kein Erlös gebucht"), with three grains that mean
 * things this app has no equivalent of. What IS shared is the visual and verbal
 * language, and that is shared on purpose: the same chip word, the same quiet
 * pill, `title` carrying the explanation rather than an `aria-describedby`
 * indirection (codex declined that on the DB table badge in PR #2752), and a
 * hatch/dash for the mark that has to survive greyscale. A farmer who has learnt
 * what `geschätzt` means on the Deckungsbeitrag page already knows what it means
 * here.
 *
 * If a third surface ever needs the same thing, the answer is to promote the
 * SHAPE (chip + title + hatch) into a shared primitive and keep the copy local —
 * not to make one app's sentences serve another's grain.
 */
import './estimateMark.css';

/** The chip word. One string, every surface — the explanation rides in ``title``. */
export const ESTIMATED_LABEL = 'geschätzt';

/**
 * What the chip means, in one sentence.
 *
 * Says what the number IS (a reference price from the curated book) and what it
 * is NOT (a booked observation), because "geschätzt" alone leaves a reader to
 * guess which of the two they are looking at — and the plausible guess is the
 * wrong one, since every other number on this page is a trade.
 */
export const ESTIMATED_TITLE = 'Referenzpreis aus dem kuratierten Preisbuch — keine gebuchte Beobachtung.';

/** The chart's legend sentence for the dashed level. */
export const ESTIMATED_LEVEL_CAPTION =
  'Gestrichelte Linie: Referenzpreis aus dem Preisbuch — geschätzt, nicht gebucht.';

/**
 * The quiet pill beside a price that is NOT a booked trade.
 *
 * Deliberately no ``aria-describedby``: the visible word carries the signal and
 * ``title`` carries the explanation, matching every other hint in this app and
 * the Deckungsbeitrag chip this mirrors.
 */
export function EstimatedChip() {
  return (
    <span className="preisspiegel-estimate-chip" title={ESTIMATED_TITLE}>
      {ESTIMATED_LABEL}
    </span>
  );
}

/**
 * The plot's dash pattern for an estimate level.
 *
 * A dash is the LINE-grain equivalent of the Deckungsbeitrag chart's 45° bar
 * hatch: it says "modelled, not measured" without colour, so it survives a
 * greyscale print and a colour-vision deficiency — where a paler stroke would
 * read as merely "less important". Kept apart from the forecast dash (``2 3``)
 * so a reader cannot confuse "carried forward from a real observation" with
 * "never observed at all".
 */
export const ESTIMATE_LEVEL_DASH = '7 4';

/** The level's stroke. A literal hex: recharts writes ``stroke`` as an SVG attribute, where ``var(--token)`` does not resolve. */
export const ESTIMATE_LEVEL_COLOR = '#1d4ed8';
